import React from 'react';
import Element, { CommonProps } from '../Element/Element';
import styles from './Table.module.scss';
import TableRow from './TableRow/TableRow';
import TableCell from './TableCell/TableCell';
import TableHeaderCell from './TableHeaderCell/TableHeaderCell';
import TableMobileHeadingCell from './TableMobileHeadingCell/TableMobileHeadingCell';
import TableSortLabel from './TableSortLabel/TableSortLabel';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { supportedBreakpointsTypes } from '../../shared/breakpoints';

const layoutOptions = ['auto', 'fixed'] as const;

type layoutOptionType = ArrayElementType<typeof layoutOptions>;

type TableProps = {
  /** Set Table layout */
  mobileBreakpoint?: supportedBreakpointsTypes;
  layout?: layoutOptionType;
  ref?: React.Ref<HTMLTableElement>;
} & Omit<CommonProps<'table'>, 'tagName'>;

const TableWithoutSubComponents: React.ForwardRefExoticComponent<TableProps> = React.forwardRef(
  (
    { mobileBreakpoint = 'sm', children, className, layout = 'auto', ...props },
    ref: React.Ref<HTMLTableElement>
  ) => {
    return (
      <Element
        tagName="table"
        ref={ref}
        {...props}
        className={[
          className,
          styles.table,
          styles.table_table,
          { [styles.table_table__auto]: layout === 'auto' },
          { [styles.table_table__fixed]: layout === 'fixed' },
          {
            [styles.table_table__mobileLayout_xxxs]:
              mobileBreakpoint === 'xxxs',
            [styles.table_table__mobileLayout_xxs]: mobileBreakpoint === 'xxs',
            [styles.table_table__mobileLayout_xs]: mobileBreakpoint === 'xs',
            [styles.table_table__mobileLayout_sm]: mobileBreakpoint === 'sm',
            [styles.table_table__mobileLayout_md]: mobileBreakpoint === 'md',
            [styles.table_table__mobileLayout_lg]: mobileBreakpoint === 'lg',
            [styles.table_table__mobileLayout_xlg]: mobileBreakpoint === 'xlg',
          },
        ]}
      >
        {children}
      </Element>
    );
  }
);

const Table: React.FunctionComponent<TableProps> & {
  Row: typeof TableRow;
  Cell: typeof TableCell;
  HeaderCell: typeof TableHeaderCell;
  MobileHeadingCell: typeof TableMobileHeadingCell;
  SortLabel: typeof TableSortLabel;
} = ({ ...props }) => <TableWithoutSubComponents {...props} />;

Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
Table.MobileHeadingCell = TableMobileHeadingCell;
Table.SortLabel = TableSortLabel;

export default Table;
